import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRulerCombined,
  faArrowTrendUp,
  faArrowTrendDown,
  faShieldHalved,
  faCoins,
  faCrosshairs,
  faArrowRight,
  faInfoCircle,
  faScaleBalanced,
  faBullseye,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import {
  BarChart,
  Bar,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
  ReferenceArea,
} from 'recharts';
import { usePageMeta } from '../../hooks/usePageMeta';
import PublicNavBar from '../../components/layout/PublicNavBar';
import PublicFooter from '../../components/layout/PublicFooter';
import {
  calculate,
  validate,
  type PositionInputs,
  type PositionResults,
  type ExitMode,
} from '../../utils/positionSizeCalculator';

// ── Scroll-reveal hook ──────────────────────────────────────────────────────

function useScrollReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, className: `transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}` };
}

// ── Defaults ────────────────────────────────────────────────────────────────

const DEFAULTS: PositionInputs = {
  accountSize: 10000,
  riskPercent: 2,
  entryPrice: 65000,
  stopLossPrice: 63000,
  exitMode: 'price' as ExitMode,
  exitPrice: 69000,
  targetRR: 2,
};

const RISK_PRESETS = [0.5, 1, 2, 3, 5];

// ── Helpers ─────────────────────────────────────────────────────────────────

function fmtDollar(n: number, decimals = 2): string {
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
}

function fmtPrice(n: number): string {
  if (n >= 1000) return fmtDollar(n, 2);
  if (n >= 1) return fmtDollar(n, 4);
  return fmtDollar(n, 6);
}

function fmtUnits(n: number): string {
  if (n >= 100) return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (n >= 1) return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  if (n >= 0.01) return n.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 });
  return n.toLocaleString(undefined, { minimumFractionDigits: 6, maximumFractionDigits: 6 });
}

// ── Number Input Component ──────────────────────────────────────────────────

interface NumberInputProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
  prefix?: string;
  suffix?: string;
  tooltip?: string;
  step?: number;
  min?: number;
  error?: string;
}

function NumberInput({ label, value, onChange, prefix, suffix, tooltip, step = 1, min = 0, error }: NumberInputProps) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-gray-300 flex items-center gap-1.5">
        {label}
        {tooltip && (
          <span className="group relative">
            <FontAwesomeIcon icon={faInfoCircle} className="text-gray-500 text-[11px] cursor-help" />
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 text-xs text-gray-200 bg-gray-800 border border-white/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
              {tooltip}
            </span>
          </span>
        )}
      </label>
      <div className={`flex items-center bg-white/[0.04] border rounded-lg overflow-hidden transition-colors
        ${error ? 'border-red-500/40' : 'border-white/[0.08] focus-within:border-[#9d4edd]/40'}`}>
        {prefix && <span className="pl-3 text-sm text-gray-500 select-none">{prefix}</span>}
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          step={step}
          min={min}
          className="w-full bg-transparent px-3 py-2.5 text-sm text-white font-mono tabular-nums outline-none
                     [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />
        {suffix && <span className="pr-3 text-sm text-gray-500 select-none">{suffix}</span>}
      </div>
      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  );
}

// ── Metric Card ─────────────────────────────────────────────────────────────

interface MetricCardProps {
  icon: typeof faCoins;
  label: string;
  value: string;
  sub?: string;
  positive?: boolean;
  negative?: boolean;
  highlight?: boolean;
}

function MetricCard({ icon, label, value, sub, positive, negative, highlight }: MetricCardProps) {
  const color = positive ? 'text-emerald-400' : negative ? 'text-red-400' : 'text-white';
  return (
    <div className={`border rounded-xl p-5 space-y-2 ${highlight
      ? 'bg-[#9d4edd]/[0.06] border-[#9d4edd]/20'
      : 'bg-white/[0.03] border-white/[0.06]'
      }`}>
      <div className="flex items-center gap-2 text-gray-400 text-sm">
        <FontAwesomeIcon icon={icon} className="text-[#9d4edd] text-xs" />
        {label}
      </div>
      <div className={`text-2xl font-bold tabular-nums ${color}`}>{value}</div>
      {sub && <div className="text-xs text-gray-500">{sub}</div>}
    </div>
  );
}

// ── Risk/Reward Visual Bar Chart ────────────────────────────────────────────

function RiskRewardChart({ results }: { results: PositionResults }) {
  const data = [
    { name: 'Risk (Stop Loss)', value: -results.potentialLoss, label: `-${fmtDollar(results.potentialLoss, 0)}` },
    { name: 'Reward (Target)', value: results.potentialProfit, label: `+${fmtDollar(results.potentialProfit, 0)}` },
  ];

  return (
    <div className="h-[200px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 10, right: 40, left: 12, bottom: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" strokeOpacity={0.8} horizontal={false} />
          <XAxis
            type="number"
            stroke="#4b5563"
            tick={{ fill: '#6b7280', fontSize: 11 }}
            tickFormatter={(v: number) => `$${Math.abs(v).toLocaleString()}`}
          />
          <YAxis
            dataKey="name"
            type="category"
            stroke="#4b5563"
            tick={{ fill: '#9ca3af', fontSize: 12 }}
            width={130}
          />
          <Tooltip
            contentStyle={{ backgroundColor: '#111827', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, fontSize: 12 }}
            labelStyle={{ color: '#9ca3af' }}
            formatter={(value: number) => [`${value >= 0 ? '+' : ''}${fmtDollar(value, 0)}`, 'P&L']}
          />
          <ReferenceLine x={0} stroke="#6b7280" strokeWidth={1} />
          <Bar dataKey="value" radius={[4, 4, 4, 4]} barSize={32}>
            {data.map((entry, idx) => (
              <Cell key={idx} fill={entry.value >= 0 ? '#22c55e' : '#ef4444'} fillOpacity={0.85} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Price Level Chart (recharts) ────────────────────────────────────────────

function PriceLevelVisual({ results, entryPrice }: { results: PositionResults; entryPrice: number }) {
  const { direction, exitPrice, stopLossDistance } = results;
  const stopPrice = direction === 'long' ? entryPrice - stopLossDistance : entryPrice + stopLossDistance;

  const minPrice = Math.min(stopPrice, entryPrice, exitPrice);
  const maxPrice = Math.max(stopPrice, entryPrice, exitPrice);
  const range = maxPrice - minPrice || 1;
  const padding = range * 0.18;

  const levels = [
    { label: 'Stop Loss', price: stopPrice, color: '#ef4444' },
    { label: 'Entry', price: entryPrice, color: '#c77dff' },
    { label: 'Target', price: exitPrice, color: '#22c55e' },
  ];

  const scatterData = levels.map((l) => ({
    x: 0,
    y: l.price,
    levelLabel: l.label,
    levelColor: l.color,
  }));

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderDot = (props: any) => {
    const { cx, cy, payload } = props;
    if (cx == null || cy == null) return null;
    const { levelLabel, levelColor, y: price } = payload;
    return (
      <g>
        <circle cx={cx} cy={cy} r={6} fill={levelColor} opacity={0.9} />
        <circle cx={cx} cy={cy} r={2.5} fill="#0a0e14" />
        <text x={cx + 16} y={cy - 5} fill={levelColor} fontSize={12} fontWeight={600}>
          {levelLabel}
        </text>
        <text x={cx + 16} y={cy + 12} fill="#6b7280" fontSize={11}>
          {fmtPrice(price)}
        </text>
      </g>
    );
  };

  return (
    <div className="h-[240px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 24, right: 24, left: 20, bottom: 24 }}>
          <XAxis type="number" dataKey="x" hide domain={[-1, 1]} />
          <YAxis
            type="number"
            dataKey="y"
            domain={[minPrice - padding, maxPrice + padding]}
            stroke="#374151"
            tick={{ fill: '#6b7280', fontSize: 11 }}
            tickFormatter={(v: number) => `$${Number(v).toLocaleString()}`}
            width={90}
          />
          <ReferenceArea
            y1={Math.min(stopPrice, entryPrice)}
            y2={Math.max(stopPrice, entryPrice)}
            fill="#ef4444"
            fillOpacity={0.06}
          />
          <ReferenceArea
            y1={Math.min(entryPrice, exitPrice)}
            y2={Math.max(entryPrice, exitPrice)}
            fill="#22c55e"
            fillOpacity={0.06}
          />
          {levels.map((l) => (
            <ReferenceLine
              key={l.label}
              y={l.price}
              stroke={l.color}
              strokeDasharray="6 4"
              strokeOpacity={0.3}
            />
          ))}
          <Scatter data={scatterData} shape={renderDot} isAnimationActive={false} />
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Main Page Component ─────────────────────────────────────────────────────

export default function PositionSizeCalculatorPage() {
  usePageMeta({
    title: 'Position Size Calculator | Quanthop Labs',
    description:
      'Free position size calculator. Enter your account size, entry price, and stop-loss to get the optimal position size, risk-reward ratio, and potential P&L.',
  });

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: 'Position Size Calculator',
      url: 'https://quanthop.com/labs/position-size-calculator',
      applicationCategory: 'FinanceApplication',
      operatingSystem: 'All',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD', availability: 'https://schema.org/InStock' },
      description: 'Free position size calculator for crypto and stock traders. Computes optimal position size based on risk percentage, stop-loss, and entry price.',
    });
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);

  const [inputs, setInputs] = useState<PositionInputs>(DEFAULTS);
  const [results, setResults] = useState<PositionResults | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const doCalculate = useCallback(() => {
    const errs = validate(inputs);
    if (errs.length > 0) {
      const errMap: Record<string, string> = {};
      errs.forEach((e) => { errMap[e.field] = e.message; });
      setErrors(errMap);
      setResults(null);
      return;
    }
    setErrors({});
    setResults(calculate(inputs));
  }, [inputs]);

  // Auto-calculate on mount and whenever inputs change
  useEffect(() => {
    const errs = validate(inputs);
    if (errs.length === 0) {
      setErrors({});
      setResults(calculate(inputs));
    }
  }, [inputs]);

  // Also calculate on first render with defaults
  useEffect(() => {
    setResults(calculate(DEFAULTS));
  }, []);

  const update = <K extends keyof PositionInputs>(field: K, value: PositionInputs[K]) => {
    setInputs((prev) => ({ ...prev, [field]: value }));
  };

  const revHero = useScrollReveal(0.1);
  const revEducation = useScrollReveal(0.1);
  const revCta = useScrollReveal(0.1);

  const directionLabel = useMemo(() => {
    if (!results) return '';
    return results.direction === 'long' ? 'Long' : 'Short';
  }, [results]);

  return (
    <div className="min-h-screen bg-[#0a0e14] text-gray-100 font-[Poppins]">
      <PublicNavBar />

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section
        ref={revHero.ref}
        className={`relative overflow-hidden pt-32 pb-16 px-4 sm:px-6 ${revHero.className}`}
      >
        <div className="max-w-4xl mx-auto text-center space-y-5">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#9d4edd]/10 border border-[#9d4edd]/20 text-[#c77dff] text-xs font-medium">
            <FontAwesomeIcon icon={faRulerCombined} className="text-[10px]" />
            Free Tool
          </div>
          <h1 className="text-3xl md:text-[42px] font-bold text-white leading-tight">
            Position Size Calculator
          </h1>
          <p className="text-gray-400/85 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Enter your account size, entry price, and stop-loss to instantly calculate
            the correct position size for any risk level. Set a target exit price to see
            your R:R, or pick a desired R:R and get the target price.
          </p>
        </div>
      </section>

      {/* ── Calculator ───────────────────────────────────────────────── */}
      <section className="pb-16 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-8">

            {/* ── Input Panel ── */}
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6 space-y-5 h-fit lg:sticky lg:top-24">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <FontAwesomeIcon icon={faCrosshairs} className="text-[#9d4edd] text-sm" />
                Trade Setup
              </h2>

              <NumberInput
                label="Account Size"
                value={inputs.accountSize}
                onChange={(v) => update('accountSize', v)}
                prefix="$"
                step={100}
                error={errors.accountSize}
              />

              {/* Risk preset buttons */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-300 flex items-center gap-1.5">
                  Risk per Trade
                  <span className="group relative">
                    <FontAwesomeIcon icon={faInfoCircle} className="text-gray-500 text-[11px] cursor-help" />
                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 text-xs text-gray-200 bg-gray-800 border border-white/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                      Percentage of account risked on this trade
                    </span>
                  </span>
                </label>
                <div className="flex gap-2">
                  {RISK_PRESETS.map((pct) => (
                    <button
                      key={pct}
                      onClick={() => update('riskPercent', pct)}
                      className={`flex-1 py-2 text-xs font-semibold rounded-lg border transition-colors
                        ${inputs.riskPercent === pct
                          ? 'bg-[#9d4edd]/20 border-[#9d4edd]/40 text-[#c77dff]'
                          : 'bg-white/[0.03] border-white/[0.06] text-gray-400 hover:border-white/10'
                        }`}
                    >
                      {pct}%
                    </button>
                  ))}
                </div>
                <NumberInput
                  label=""
                  value={inputs.riskPercent}
                  onChange={(v) => update('riskPercent', v)}
                  suffix="%"
                  step={0.5}
                  min={0.1}
                  error={errors.riskPercent}
                />
              </div>

              <div className="border-t border-white/[0.06] pt-4 space-y-4">
                <NumberInput
                  label="Entry Price"
                  value={inputs.entryPrice}
                  onChange={(v) => update('entryPrice', v)}
                  prefix="$"
                  step={100}
                  error={errors.entryPrice}
                />
                <NumberInput
                  label="Stop-Loss Price"
                  value={inputs.stopLossPrice}
                  onChange={(v) => update('stopLossPrice', v)}
                  prefix="$"
                  step={100}
                  tooltip="Price at which you exit to limit losses"
                  error={errors.stopLossPrice}
                />
              </div>

              {/* Exit mode toggle */}
              <div className="border-t border-white/[0.06] pt-4 space-y-3">
                <div className="flex rounded-lg overflow-hidden border border-white/[0.08]">
                  <button
                    onClick={() => update('exitMode', 'price')}
                    className={`flex-1 py-2 text-xs font-semibold transition-colors
                      ${inputs.exitMode === 'price'
                        ? 'bg-[#9d4edd]/20 text-[#c77dff]'
                        : 'bg-white/[0.02] text-gray-400 hover:text-gray-300'
                      }`}
                  >
                    Set Exit Price
                  </button>
                  <button
                    onClick={() => update('exitMode', 'rr')}
                    className={`flex-1 py-2 text-xs font-semibold transition-colors
                      ${inputs.exitMode === 'rr'
                        ? 'bg-[#9d4edd]/20 text-[#c77dff]'
                        : 'bg-white/[0.02] text-gray-400 hover:text-gray-300'
                      }`}
                  >
                    Set R:R Ratio
                  </button>
                </div>

                {inputs.exitMode === 'price' ? (
                  <NumberInput
                    label="Target Exit Price"
                    value={inputs.exitPrice}
                    onChange={(v) => update('exitPrice', v)}
                    prefix="$"
                    step={100}
                    error={errors.exitPrice}
                  />
                ) : (
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-300">Target R:R Ratio</label>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center bg-white/[0.04] border border-white/[0.08] rounded-lg overflow-hidden w-20">
                        <span className="pl-3 text-sm text-gray-500 select-none">R</span>
                        <input
                          type="number"
                          value={1}
                          disabled
                          className="w-full bg-transparent px-2 py-2.5 text-sm text-gray-500 font-mono tabular-nums outline-none cursor-not-allowed
                                     [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                      </div>
                      <span className="text-gray-500 font-semibold text-lg">:</span>
                      <div className={`flex items-center bg-white/[0.04] border rounded-lg overflow-hidden flex-1 transition-colors
                        ${errors.targetRR ? 'border-red-500/40' : 'border-white/[0.08] focus-within:border-[#9d4edd]/40'}`}>
                        <span className="pl-3 text-sm text-gray-500 select-none">R</span>
                        <input
                          type="number"
                          value={inputs.targetRR}
                          onChange={(e) => update('targetRR', parseFloat(e.target.value) || 0)}
                          step={0.5}
                          min={0.1}
                          className="w-full bg-transparent px-2 py-2.5 text-sm text-white font-mono tabular-nums outline-none
                                     [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                      </div>
                    </div>
                    {errors.targetRR && <p className="text-xs text-red-400">{errors.targetRR}</p>}
                  </div>
                )}
              </div>

              <button
                onClick={doCalculate}
                className="w-full px-6 py-3 rounded-xl bg-[#9d4edd] hover:bg-[#8b3ec7] text-white text-sm font-semibold transition-colors flex items-center justify-center gap-2"
              >
                <FontAwesomeIcon icon={faRulerCombined} className="text-xs" />
                Calculate Position
              </button>
            </div>

            {/* ── Results Panel ── */}
            <div className="space-y-6">
              {results && (
                <>
                  {/* Direction badge */}
                  <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-semibold
                    ${results.direction === 'long'
                      ? 'bg-emerald-500/[0.06] border-emerald-500/20 text-emerald-400'
                      : 'bg-red-500/[0.06] border-red-500/20 text-red-400'
                    }`}
                  >
                    <FontAwesomeIcon icon={results.direction === 'long' ? faArrowTrendUp : faArrowTrendDown} />
                    {directionLabel} Position
                  </div>

                  {/* Key metrics */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <MetricCard
                      icon={faRulerCombined}
                      label="Position Size"
                      value={fmtDollar(results.positionSizeUsd, 0)}
                      sub={`${(results.leverage * 100).toFixed(1)}% of account`}
                      highlight
                    />
                    <MetricCard
                      icon={faScaleBalanced}
                      label="Risk : Reward"
                      value={results.riskRewardLabel}
                      sub={`${results.rewardRiskRatio.toFixed(1)}R`}
                      positive={results.rewardRiskRatio >= 2}
                      negative={results.rewardRiskRatio < 1}
                    />
                    <MetricCard
                      icon={faShieldHalved}
                      label="Risk Amount"
                      value={fmtDollar(results.riskAmount, 0)}
                      sub={`${results.potentialLossPct.toFixed(1)}% of account`}
                      negative
                    />
                    <MetricCard
                      icon={faCoins}
                      label="Potential Profit"
                      value={`+${fmtDollar(results.potentialProfit, 0)}`}
                      sub={`+${results.potentialProfitPct.toFixed(1)}% of account`}
                      positive
                    />
                    <MetricCard
                      icon={faBullseye}
                      label={inputs.exitMode === 'rr' ? 'Computed Exit' : 'Target Exit'}
                      value={fmtPrice(results.exitPrice)}
                      sub={`SL distance: ${results.stopLossDistancePct.toFixed(2)}%`}
                    />
                    <MetricCard
                      icon={faTriangleExclamation}
                      label="Leverage"
                      value={`${results.leverage.toFixed(2)}x`}
                      sub={results.leverage > 1 ? 'Position exceeds account' : 'No leverage needed'}
                      negative={results.leverage > 3}
                    />
                  </div>

                  {/* Visual: Price levels */}
                  <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6 space-y-4">
                    <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                      <FontAwesomeIcon icon={faCrosshairs} className="text-[#9d4edd] text-sm" />
                      Price Levels
                    </h2>
                    <PriceLevelVisual results={results} entryPrice={inputs.entryPrice} />
                  </div>

                  {/* Risk/Reward bar chart */}
                  <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6 space-y-4">
                    <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                      <FontAwesomeIcon icon={faScaleBalanced} className="text-[#9d4edd] text-sm" />
                      Risk vs Reward
                    </h2>
                    <RiskRewardChart results={results} />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── Education ────────────────────────────────────────────────── */}
      <section
        ref={revEducation.ref}
        className={`py-20 px-4 sm:px-6 border-t border-white/[0.06] ${revEducation.className}`}
      >
        <div className="max-w-4xl mx-auto space-y-12">
          <h2 className="text-[22px] font-semibold text-white text-center">
            Why position sizing matters
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                title: 'Risk Per Trade',
                body: 'Professional traders typically risk 1-2% of their account per trade. This ensures no single loss can significantly damage the account. Even a string of 10 losses only draws down 10-20%.',
              },
              {
                title: 'Stop-Loss Distance',
                body: 'The distance between your entry and stop-loss determines how many units you can buy. A wider stop means fewer units (smaller position) for the same dollar risk. Tighter stops allow larger positions but get hit more often.',
              },
              {
                title: 'Risk-Reward Ratio',
                body: 'A 1:2 R:R means you stand to gain $2 for every $1 risked. With a 1:2 ratio, you only need to win 34% of trades to break even. Higher ratios give your strategy more room for losing streaks.',
              },
              {
                title: 'Leverage Awareness',
                body: 'When position value exceeds account size, you are using leverage. A 5x leveraged position means a 20% adverse move wipes out the account. Always know your effective leverage before entering a trade.',
              },
            ].map(({ title, body }) => (
              <div key={title} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5 space-y-2">
                <h3 className="text-sm font-semibold text-white">{title}</h3>
                <p className="text-sm text-gray-400/85 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────────── */}
      <section
        ref={revCta.ref}
        className={`py-20 px-4 sm:px-6 border-t border-white/[0.06] ${revCta.className}`}
      >
        <div className="max-w-2xl mx-auto text-center space-y-5">
          <h2 className="text-2xl font-bold text-white">
            Want to backtest these position sizing rules?
          </h2>
          <p className="text-gray-400/85">
            Quanthop lets you define custom risk rules, backtest them on historical data,
            and validate with Walk-Forward Analysis before risking real capital.
          </p>
          <a
            href="/pricing"
            className="inline-flex items-center gap-2 px-7 py-3 rounded-xl bg-[#9d4edd] hover:bg-[#8b3ec7] text-white text-sm font-semibold transition-colors"
          >
            See Pricing
            <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
          </a>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
